<template>
  <Menu as="div" class="relative inline-block text-left sm:flex-1 lg:flex-none lg:w-auto">
    <MenuButton
      class="relative w-full disabled:bg-gray-400 inline-flex h-8 items-center px-2 py-2 border border-gray-700 shadow-sm text-sm font-medium rounded-md text-text-primary bg-gray-800 hover:bg-gray-700"
    >
      <div class="flex-1 text-left font-light flex gap-1">
        <span class="text-text-secondary">{{ $t('home.filters.map_label') }}</span>
        <span v-if="selectedMap">{{ selectedMap.friendly_name }}</span>
        <span v-else>{{ $t('common.all') }}</span>
      </div>
      <ChevronDownIcon class="-mr-1 ml-4 h-5 w-5" aria-hidden="true" />
    </MenuButton>

    <transition
      enter-active-class="transition ease-out duration-100"
      enter-from-class="transform opacity-0 scale-95"
      enter-to-class="transform opacity-100 scale-100"
      leave-active-class="transition ease-in duration-75"
      leave-from-class="transform opacity-100 scale-100"
      leave-to-class="transform opacity-0 scale-95"
    >
      <MenuItems
        class="absolute right-0 z-50 mt-2 w-full lg:w-[192px] origin-top-right rounded-md bg-gray-800 border border-gray-700 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
      >
        <MenuItem @click="(e) => { updatedSelectedMap(e, null); }" class="px-4 pt-3 hover:bg-gray-700">
          <a
            href="#"
            :class="[
              !selectedMap ? 'text-text-primary' : 'text-text-secondary',
              'flex items-center py-2 text-sm w-full justify-start font-light',
            ]"
          >
            {{ $t('home.filters.all_maps') }}
            <CheckIcon v-if="!selectedMap" class="h-5 w-5 ml-auto text-primary-400" />
          </a>
        </MenuItem>
        <MenuItem
          class="px-4 last:pb-3 hover:bg-gray-700"
          :key="map"
          v-for="map in maps"
          @click="(e) => { updatedSelectedMap(e, map); }"
        >
          <a
            href="#"
            :class="[
              selectedMap == map ? 'text-text-primary' : 'text-text-secondary',
              'flex items-center py-2 text-sm w-full justify-start',
            ]"
          >
            {{ map.friendly_name }}
            <CheckIcon v-if="map === selectedMap" class="h-5 w-5 ml-auto text-primary-400" />
          </a>
        </MenuItem>
      </MenuItems>
    </transition>
  </Menu>
</template>

<script setup>
/**
 * MapDropdown - A reusable dropdown component for selecting maps.
 * It triggers an event when the map changes, and auto-updates the URL with the new value.
 * Current selected map is readable through a ref access from the parent.
 *
 * @example
 * // In parent component:
 * <template>
 *   <MapDropdown ref="mapDropdownRef" @selectedMapChange="handleMapChange" />
 * </template>
 * <script setup>
 *   const mapDropdownRef = ref(null);
 *   function handleMapChange() {
 *     console.log('Selected map:', mapDropdownRef.value.selectedMap?.name);
 *   }
 * <\/script>
 */

import {
  Menu, MenuButton, MenuItems, MenuItem,
} from '@headlessui/vue';
import { ChevronDownIcon, CheckIcon } from '@heroicons/vue/24/solid';
import {
  ref, onMounted, defineEmits, defineExpose,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';

const QUERY_PARAM_NAME = 'map'; // e.g. `?map=de_dust2`

const route = useRoute();
const router = useRouter();
const store = useStore();

const maps = ref([]);
const selectedMap = ref(null);
const emit = defineEmits(['selectedMapChange']);
defineExpose({ selectedMap });

function updatedSelectedMap(e, map) {
  e.preventDefault();
  selectedMap.value = map;
  updateQuery(map);

  // NOTE(ssaunier): parent can use a ref and access exposed `selectedMap` to get the value, no need to emit it as an argument.
  emit('selectedMapChange');
}

function updateQuery(map) {
  const nextQuery = { ...route.query };
  if (map) {
    nextQuery[QUERY_PARAM_NAME] = map.name;
  } else {
    delete nextQuery[QUERY_PARAM_NAME];
  }
  router.replace({ query: nextQuery });
}

// NOTE(ssaunier): The Dropdown menu will list all maps linked to the `competitive` mod through a back-end `ModMap` record.
function setMapsFromStore() {
  const COMPETITIVE_MOD_NAME = 'competitive'; // cf ForestAdmin: https://app.forestadmin.com/Refrag/Production/Development/data/Mod/index/record/Mod/39
  const mod = store.getters.modInfo?.mods?.find((mod) => mod.name === COMPETITIVE_MOD_NAME);
  if (mod) {
    maps.value = [...mod.cs_maps].sort((a, b) => a.friendly_name.localeCompare(b.friendly_name));
  }
}

function setSelectedMapFromQuery() {
  selectedMap.value = maps.value.find((map) => map.name === route.query[QUERY_PARAM_NAME]) || null;
  if (selectedMap.value) {
    emit('selectedMapChange');
  }
}

onMounted(() => {
  setMapsFromStore();
  setSelectedMapFromQuery();
});
</script>
